import React from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import GuideInfoBox from '../components/GuideInfoBox';
import GuideMockButton from '../components/GuideMockButton';
import GuideMockContainer from '../components/GuideMockContainer';
import GuidePageLayout from '../components/GuidePageLayout';
import GuideSection from '../components/GuideSection';
import GuideStepList from '../components/GuideStepList';
import GuideTable from '../components/GuideTable';

const GuideMemberUpdate: React.FC = () => {
  return (
    <GuidePageLayout
      title="要員情報を更新する"
      description="既存要員の情報を変更します。"
      breadcrumbs={[{ label: '要員管理', path: '/guide/member/overview' }]}
    >
      <GuideSection title="前提">
        <GuideTable
          headers={['条件', '内容']}
          rows={[
            ['必要な権限', '管理者 / 管理部門 / 課長'],
            ['ログイン状態', 'ログイン済みであること'],
            ['対象要員', '存在し、削除済みでないこと'],
          ]}
        />
      </GuideSection>

      <GuideSection title="画面モード">
        <GuideTable
          headers={['モード', 'タイトル', '説明']}
          rows={[
            ['参照モード', '要員詳細', '要員情報を参照専用で表示。編集ボタンで編集モードに切替'],
            [
              '編集モード',
              '要員詳細（編集中）',
              '各フィールドが入力可能になる。戻る矢印で参照モードに戻る',
            ],
          ]}
        />
      </GuideSection>

      <GuideSection title="手順">
        <GuideStepList
          steps={[
            { label: '要員管理画面を開く' },
            { label: '要員一覧から対象要員を検索・選択する' },
            {
              label: '参照モード（要員詳細）が表示される',
              description:
                'ヘッダー右上に要員IDが表示されます。コピーボタンでクリップボードにコピーできます。',
              content: (
                <GuideMockContainer label="参照モード（ヘッダー部分）">
                  <Stack spacing={1}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ pointerEvents: 'none' }}
                    >
                      <IconButton size="small">
                        <ArrowBackIcon fontSize="small" />
                      </IconButton>
                      <Typography variant="h6" fontWeight={700}>
                        要員詳細
                      </Typography>
                      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ ml: 'auto' }}>
                        <Typography variant="caption" color="text.secondary">
                          ID: xxxxxxxx-xxxx-xxxx-xxxx
                        </Typography>
                        <IconButton size="small">
                          <ContentCopyIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Stack>
                    </Stack>
                  </Stack>
                </GuideMockContainer>
              ),
            },
            {
              label: '「編集」ボタンを押下して編集モードに切り替える',
              content: (
                <GuideMockContainer label="参照モード フッター">
                  <GuideMockButton label="編集" startIcon={<EditIcon />} />
                </GuideMockContainer>
              ),
            },
            {
              label: '編集フォームで変更したい項目を修正する',
              description: '雇用形態に応じて編集可能な項目が異なります。',
              content: (
                <GuideMockContainer label="編集モード（ヘッダー部分）">
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ pointerEvents: 'none' }}
                  >
                    <IconButton size="small">
                      <ArrowBackIcon fontSize="small" />
                    </IconButton>
                    <Typography variant="h6" fontWeight={700}>
                      要員詳細（編集中）
                    </Typography>
                  </Stack>
                </GuideMockContainer>
              ),
            },
            {
              label: '「更新」ボタンを押下する',
              content: (
                <GuideMockContainer label="編集モード フッター">
                  <GuideMockButton label="更新" startIcon={<SaveIcon />} />
                </GuideMockContainer>
              ),
            },
          ]}
        />
      </GuideSection>

      <GuideSection title="編集可能な項目">
        <GuideTable
          headers={['項目名', '社員', '社外', '備考']}
          rows={[
            ['要員名', '○', '○', ''],
            ['要員名カナ', '○', '○', ''],
            ['性別', '○', '○', ''],
            ['ユーザーID', '変更不可', '—', '社員は登録時に確定'],
            ['所属', '○', '—', '社員のみ'],
            ['契約形態', '—', '○', '社外のみ変更可'],
            ['備考', '○', '○', '6行表示'],
          ]}
        />
      </GuideSection>

      <GuideSection title="ボタンと操作">
        <GuideTable
          headers={['操作', 'モード', '動作']}
          rows={[
            ['← 戻る矢印（参照モード）', '参照', '詳細画面を閉じて一覧に戻る'],
            ['IDコピーボタン', '共通', '要員IDをクリップボードにコピー'],
            ['削除ボタン', '参照', '要員を論理削除する（確認ダイアログあり）'],
            ['編集ボタン', '参照', '編集モードに切り替える'],
            ['← 戻る矢印（編集モード）', '編集', '編集内容を破棄して参照モードに戻る'],
            ['更新ボタン', '編集', '入力内容で要員情報を更新する'],
          ]}
        />
      </GuideSection>

      <GuideSection title="注意事項">
        <Stack spacing={1}>
          <GuideInfoBox type="warning">雇用形態は登録時に確定するため変更できません。</GuideInfoBox>
          <GuideInfoBox type="warning">
            社員の場合、ユーザーは登録時に確定するため変更できません。要員名は変更可能です。
          </GuideInfoBox>
          <GuideInfoBox type="warning">
            削除済みの要員は編集できません（編集ボタンが非表示）。
          </GuideInfoBox>
          <GuideInfoBox>
            編集モードで戻る矢印をクリックすると、未保存の変更は破棄されます。
          </GuideInfoBox>
        </Stack>
      </GuideSection>
    </GuidePageLayout>
  );
};

export default GuideMemberUpdate;
